// @workflow platform.viewer
/**
 * The 3D model's links to the other views: the level a point is on, the 2D plan, the 360
 * scan nearest a point, and the 360 open on screen. Every operation forwards to today's implementation,
 * except that `openScan` also requires the 360 pane to be visible (AVA-750).
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { PANE_TYPE_360 } from './core.js';

/**
 * The page shows its pane of this type ('360', 'doc'): MainView's slot of that type (`getViewTypeIndex`, 0 when
 * there is none) is visible. The single-view layout hides a pane without destroying what it shows.
 */
export function isPaneVisible(mainApp, paneType) {
  if (!mainApp || typeof mainApp.getViewTypeIndex !== 'function') return false;
  const index = mainApp.getViewTypeIndex(paneType);
  return index > 0 && !!mainApp[`isView${index}Visible`];
}

/** @param {object} ctx see core.js `createViewer` */
export function createViewLinkOps(ctx) {
  const { store, service } = ctx;

  return {
    /** forgeService.getLevelContainsPoint: the facility level whose elevation holds `point.z`, or null. */
    levelContaining(point) {
      return point ? service().getLevelContainsPoint(point) : null;
    },
    /** The 2D plan's labels extension createHighlightingTags (forgeService.getLabels2DExtension). */
    highlightTagsIn2d(tags) {
      const ext = service().getLabels2DExtension();
      if (!ext) return false;
      ext.createHighlightingTags(tags);
      return true;
    },
    /**
     * The store's `zoomToPointIn2D` action: the 2D view on the plan of the level holding a 3D point,
     * zoomed to it. `levelName` picks the level first; `zoom: false` leaves the framing to `callback`
     * (a highlighted single tag is framed by the labels extension). Answers its PLAN_ZOOM_STATUS.
     */
    zoomToPointIn2d(point3d, callback, { levelName, zoom = true } = {}) {
      const levelPart = levelName === undefined ? {} : { levelName };
      return store.dispatch('zoomToPointIn2D', { point3d, ...levelPart, shouldZoom: zoom, callback });
    },
    /** The 2D plan the page shows: its urn (`state.planUrn`) and whether its model has loaded. */
    plan2d() {
      const planViewer = store.state.planViewer;
      return { urn: store.state.planUrn || null, loaded: !!(planViewer && planViewer.model) };
    },
    /** forgeService.getClosestImageToPoint: the 360 scan nearest a viewer-space point, or null. */
    closestImageTo(viewerPoint) {
      return viewerPoint ? service().getClosestImageToPoint(viewerPoint) : null;
    },
    /**
     * The 360 open on screen as `{ id, name }` (AVA-750), or null: the image the panorama viewer shows
     * (forgeService.launchPanoViewerHandler sets `panoramaViewer.forgeImage`; the viewer is destroyed when the 360 closes),
     * only while its pane is visible: a panorama left in a pane the layout hid is not on screen.
     */
    openScan() {
      if (!isPaneVisible(store.state.mainApp, PANE_TYPE_360)) return null;
      const image = service()?.panoramaViewer?.forgeImage;
      if (!image || image.id == null) return null;
      return { id: String(image.id), name: image.name || null };
    },
  };
}
